import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../lib/apiClient';
import { useCustomers } from '../context/CustomerContext';
import slaReferenceImg from '@assets/image_1777215282740.png';

interface Policy { severity: string; ttaMinutes: number; ttrMinutes: number; isDefault?: boolean; }

const SEVERITIES = ['Critical', 'High', 'Medium', 'Low', 'Informational'] as const;

export function SlaPolicyEditor() {
  const { customers, activeCustomerId, setActiveCustomerId } = useCustomers();
  const [policies, setPolicies] = useState<Policy[]>(SEVERITIES.map((s) => ({ severity: s, ttaMinutes: 60, ttrMinutes: 1440 })));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<{ policies: Policy[] }>(`/sla?customerId=${encodeURIComponent(activeCustomerId)}`);
      setPolicies(data.policies);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load SLA policies');
    } finally {
      setLoading(false);
    }
  }, [activeCustomerId]);

  useEffect(() => { load(); }, [load]);

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await apiFetch('/sla', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ customerId: activeCustomerId, policies: policies.map((p) => ({ severity: p.severity, ttaMinutes: p.ttaMinutes, ttrMinutes: p.ttrMinutes })) }),
      });
      setSavedAt(new Date().toLocaleTimeString());
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  function update(idx: number, field: 'ttaMinutes' | 'ttrMinutes', value: string) {
    const v = Math.max(1, Number(value) || 0);
    setPolicies((prev) => prev.map((p, i) => i === idx ? { ...p, [field]: v, isDefault: false } : p));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-foreground">SLA Policies</h3>
        <select className="input-default text-xs" value={activeCustomerId} onChange={(e) => setActiveCustomerId(e.target.value)}>
          {customers.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
        </select>
      </div>
      <p className="text-xs text-muted-foreground">Time-to-Acknowledge (TTA / MTTD) and Time-to-Resolve (TTR / MTTR) targets per severity, in minutes. Defaults below follow the Kloudsecure standard SLA. Edit any row to override for this specific customer.</p>

      <div className="rounded-lg border border-border bg-muted/10 overflow-hidden">
        <img
          src={slaReferenceImg}
          alt="Kloudsecure standard SLA thresholds reference table"
          className="w-full h-auto block"
        />
      </div>

      {error && <div className="px-3 py-2 bg-red-500/20 text-red-700 dark:text-red-300 text-xs rounded-lg">{error}</div>}
      {loading && <div className="text-xs text-muted-foreground">Loading…</div>}

      <div className="grid gap-3">
        {policies.map((p, idx) => (
          <div key={p.severity} className="grid grid-cols-[120px_1fr_1fr_auto] items-center gap-3 p-3 rounded-lg border border-border bg-muted/20">
            <div className="text-sm font-semibold text-foreground">{p.severity}</div>
            <label className="text-xs text-muted-foreground">
              TTA (min)
              <input type="number" min={1} value={p.ttaMinutes} onChange={(e) => update(idx, 'ttaMinutes', e.target.value)} className="input-default mt-1 w-full" />
            </label>
            <label className="text-xs text-muted-foreground">
              TTR (min)
              <input type="number" min={1} value={p.ttrMinutes} onChange={(e) => update(idx, 'ttrMinutes', e.target.value)} className="input-default mt-1 w-full" />
            </label>
            <span className="text-[10px] text-muted-foreground italic">{p.isDefault ? 'default' : 'custom'}</span>
          </div>
        ))}
      </div>

      <div className="flex items-center gap-3">
        <button onClick={save} disabled={saving} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save Policies'}
        </button>
        {savedAt && <span className="text-xs text-green-500">Saved at {savedAt}</span>}
      </div>
    </div>
  );
}
